import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ChatbotSettings.css';
import './TicketFormSettings.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaPlus, FaTrash, FaSpinner, FaSave, FaPalette, FaListUl,
  FaCommentDots, FaCode, FaCheck, FaChevronDown, FaChevronRight,
  FaArrowUp, FaArrowDown, FaUndo, FaEye, FaEyeSlash, FaCopy, FaGlobe
} from 'react-icons/fa';
import TicketFormPreview from './TicketFormPreview';

// Must match SUPPORTED_FORM_LANGUAGES in the backend
// (app/services/form_translation_service.py) — the languages the embedded
// widget's own chrome is translated into. Labels are the native names.
export const SUPPORTED_FORM_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
];

export const DEFAULT_SUBJECTS = [
  'Retard de livraison',
  'Demande de localisation',
  'Marchandise manquante',
  'Marchandise endommagée',
  'Colis perdu',
  'Point Relais non respecté',
  'Contestation de livraison',
  'Anomalie douanière',
  'Multi-colis non respecté',
  "Complément d'adresse",
  'Demande de preuve de livraison',
  'Retour non reçu',
  'Demande de retour colis',
  "Anomalie d'enlèvement",
  'Facturation et paiement',
  'Autre',
];

const clampRadius = (value) => {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.min(32, Math.max(0, parsed));
};

// Keep aligned with the backend defaults (_DEFAULTS in
// app/api/ticket_form_settings.py); empty texts mean "use the widget's own
// localized wording".
const DEFAULT_SETTINGS = {
  primary_color: '#1f2937',
  background_color: '#ffffff',
  text_color: '#1f2937',
  accent_color: '#2563eb',
  border_radius: 12,
  title: '',
  intro_text: '',
  success_message: '',
  button_label: '',
  displayed: true,
  show_order_fields: true,
  classify_inbound_subjects: true,
  subjects: [...DEFAULT_SUBJECTS],
  languages: [],
};

const TicketFormSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [settingsList, setSettingsList] = useState([]);
  const [brands, setBrands] = useState([]);
  // '' = brandless default form, otherwise a brand id
  const [selectedBrandKey, setSelectedBrandKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [newSubject, setNewSubject] = useState('');

  const [expandedSections, setExpandedSections] = useState({
    texts: true,
    design: true,
    subjects: true,
    languages: true,
    embed: false,
  });

  const [editSettings, setEditSettings] = useState({ ...DEFAULT_SETTINGS });

  const extractSettings = (doc) => ({
    primary_color: doc.primary_color || DEFAULT_SETTINGS.primary_color,
    background_color: doc.background_color || DEFAULT_SETTINGS.background_color,
    text_color: doc.text_color || DEFAULT_SETTINGS.text_color,
    accent_color: doc.accent_color || DEFAULT_SETTINGS.accent_color,
    border_radius: clampRadius(doc.border_radius ?? DEFAULT_SETTINGS.border_radius),
    title: doc.title ?? DEFAULT_SETTINGS.title,
    intro_text: doc.intro_text ?? DEFAULT_SETTINGS.intro_text,
    success_message: doc.success_message ?? DEFAULT_SETTINGS.success_message,
    button_label: doc.button_label ?? DEFAULT_SETTINGS.button_label,
    displayed: doc.displayed ?? true,
    show_order_fields: doc.show_order_fields ?? true,
    classify_inbound_subjects: doc.classify_inbound_subjects ?? true,
    subjects: Array.isArray(doc.subjects) && doc.subjects.length > 0
      ? [...doc.subjects]
      : [...DEFAULT_SUBJECTS],
    languages: Array.isArray(doc.languages) ? [...doc.languages] : [],
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchInitialData(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchInitialData = async (token) => {
    try {
      setIsLoading(true);
      const userData = await fetchUserData(token);
      const list = await fetchFormSettings(token);
      if (userData?.has_brand) {
        await fetchBrands(token);
      }
      const defaultDoc = (list || []).find((doc) => !doc.brand_id);
      if (defaultDoc) {
        setSelectedBrandKey('');
        setEditSettings(extractSettings(defaultDoc));
      }
    } catch (err) {
      console.error('Failed to fetch ticket form settings:', err);
      setError(t('ticketFormSettings.loadError', 'Échec du chargement des paramètres du formulaire'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserData = async (token) => {
    const response = await axios.get('/api/users/user', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  const fetchFormSettings = async (token) => {
    const response = await axios.get('/api/tickets/form-settings', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const list = response.data || [];
    setSettingsList(list);
    return list;
  };

  const fetchBrands = async (token) => {
    try {
      const response = await axios.get('/api/brands/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(response.data || []);
    } catch {
      setBrands([]);
    }
  };

  const docForBrandKey = (brandKey) =>
    settingsList.find((doc) => (doc.brand_id || '') === brandKey);

  const currentDoc = docForBrandKey(selectedBrandKey);

  // Tabs: brandless default + one entry per brand (+ orphan docs whose brand
  // no longer exists, kept visible so existing configs never disappear).
  const brandEntries = [
    { key: '', name: t('ticketFormSettings.defaultForm', 'Formulaire par défaut') },
    ...brands.map((brand) => ({ key: brand.id, name: brand.name })),
    ...settingsList
      .filter((doc) => doc.brand_id && !brands.some((b) => b.id === doc.brand_id))
      .map((doc) => ({
        key: doc.brand_id,
        name: t('ticketFormSettings.unknownBrand', 'Marque inconnue'),
      })),
  ];

  const handleSelectBrand = (brandKey) => {
    if (brandKey === selectedBrandKey) return;
    if (hasUnsavedChanges) {
      if (!window.confirm(t('ticketFormSettings.unsavedWarning', 'Vous avez des modifications non enregistrées. Les annuler ?'))) {
        return;
      }
    }
    setSelectedBrandKey(brandKey);
    const doc = docForBrandKey(brandKey);
    setEditSettings(doc ? extractSettings(doc) : { ...DEFAULT_SETTINGS });
    setHasUnsavedChanges(false);
    setError(null);
    setSuccess(null);
    setShowEmbedCode(false);
    setNewSubject('');
  };

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (name === 'border_radius') {
      newValue = clampRadius(value);
    } else {
      newValue = value;
    }
    setEditSettings((prev) => ({ ...prev, [name]: newValue }));
    setHasUnsavedChanges(true);
  }, []);

  /* ----- Subjects ----- */

  const handleSubjectChange = (index, value) => {
    setEditSettings((prev) => {
      const subjects = [...prev.subjects];
      subjects[index] = value;
      return { ...prev, subjects };
    });
    setHasUnsavedChanges(true);
  };

  const handleRemoveSubject = (index) => {
    setEditSettings((prev) => {
      if (prev.subjects.length <= 1) return prev;
      return { ...prev, subjects: prev.subjects.filter((_, i) => i !== index) };
    });
    setHasUnsavedChanges(true);
  };

  const handleMoveSubject = (index, direction) => {
    setEditSettings((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.subjects.length) return prev;
      const subjects = [...prev.subjects];
      [subjects[index], subjects[target]] = [subjects[target], subjects[index]];
      return { ...prev, subjects };
    });
    setHasUnsavedChanges(true);
  };

  const handleAddSubject = () => {
    const value = newSubject.trim();
    if (!value) return;
    const exists = editSettings.subjects.some(
      (s) => s.trim().toLowerCase() === value.toLowerCase()
    );
    if (exists) {
      setError(t('ticketFormSettings.duplicateSubject', 'Ce sujet existe déjà dans la liste'));
      return;
    }
    setEditSettings((prev) => ({ ...prev, subjects: [...prev.subjects, value] }));
    setNewSubject('');
    setError(null);
    setHasUnsavedChanges(true);
  };

  const handleToggleLanguage = (code) => {
    setEditSettings((prev) => ({
      ...prev,
      languages: prev.languages.includes(code)
        ? prev.languages.filter((c) => c !== code)
        : [...prev.languages, code],
    }));
    setHasUnsavedChanges(true);
  };

  const handleResetSubjects = () => {
    setEditSettings((prev) => ({ ...prev, subjects: [...DEFAULT_SUBJECTS] }));
    setHasUnsavedChanges(true);
  };

  const validateSubjects = () => {
    const cleaned = editSettings.subjects.map((s) => s.trim());
    if (cleaned.length === 0 || cleaned.some((s) => !s)) {
      setError(t('ticketFormSettings.emptySubject', 'Aucun sujet ne peut être vide'));
      return null;
    }
    const lowered = cleaned.map((s) => s.toLowerCase());
    if (new Set(lowered).size !== lowered.length) {
      setError(t('ticketFormSettings.duplicateSubject', 'Ce sujet existe déjà dans la liste'));
      return null;
    }
    return cleaned;
  };

  /* ----- Save / delete / embed ----- */

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    const subjects = validateSubjects();
    if (!subjects) return;

    setIsSaving(true);
    const token = localStorage.getItem('access_token');
    const payload = {
      primary_color: editSettings.primary_color,
      background_color: editSettings.background_color,
      text_color: editSettings.text_color,
      accent_color: editSettings.accent_color,
      border_radius: clampRadius(editSettings.border_radius),
      title: editSettings.title,
      intro_text: editSettings.intro_text,
      success_message: editSettings.success_message,
      button_label: editSettings.button_label,
      displayed: !!editSettings.displayed,
      show_order_fields: !!editSettings.show_order_fields,
      classify_inbound_subjects: !!editSettings.classify_inbound_subjects,
      subjects,
      languages: editSettings.languages,
    };

    try {
      if (currentDoc) {
        await axios.put(`/api/tickets/form-settings/${currentDoc.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess(t('ticketFormSettings.updateSuccess', 'Paramètres enregistrés avec succès'));
      } else {
        await axios.post(
          '/api/tickets/form-settings',
          { ...payload, brand_id: selectedBrandKey || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(t('ticketFormSettings.createSuccess', 'Formulaire créé avec succès'));
      }
      await fetchFormSettings(token);
      setHasUnsavedChanges(false);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Failed to save ticket form settings:', err);
      if (err.response?.status === 409) {
        setError(t('ticketFormSettings.alreadyExists', 'Un formulaire existe déjà pour cette marque. Rechargement...'));
        await fetchFormSettings(token);
      } else {
        setError(err.response?.data?.detail || t('ticketFormSettings.saveError', "Échec de l'enregistrement des paramètres"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentDoc) return;
    if (!window.confirm(t('ticketFormSettings.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce formulaire ?'))) {
      return;
    }
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`/api/tickets/form-settings/${currentDoc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = await fetchFormSettings(token);
      const doc = (list || []).find((d) => (d.brand_id || '') === selectedBrandKey);
      setEditSettings(doc ? extractSettings(doc) : { ...DEFAULT_SETTINGS });
      setHasUnsavedChanges(false);
      setSuccess(t('ticketFormSettings.deleteSuccess', 'Formulaire supprimé avec succès'));
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Failed to delete ticket form settings:', err);
      setError(t('ticketFormSettings.deleteError', 'Échec de la suppression du formulaire'));
    }
  };

  const handleCopyEmbedCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = selectedBrandKey ? `?brand_id=${selectedBrandKey}` : '';
      const response = await axios.get(`/api/tickets/embed${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const code = response.data.embed_code;
      setEmbedCode(code);
      setShowEmbedCode(true);
      setExpandedSections((prev) => ({ ...prev, embed: true }));
      navigator.clipboard.writeText(code);
      setSuccess(t('ticketFormSettings.copySuccess', "Code d'intégration copié dans le presse-papiers"));
      setTimeout(() => setSuccess(null), 4000);
    } catch {
      setError(t('ticketFormSettings.embedError', "Échec de la génération du code d'intégration"));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderSectionHeader = (key, icon, label) => (
    <button type="button" className="cs-section-header" onClick={() => toggleSection(key)}>
      <div className="cs-section-header-left">
        <span className="cs-section-icon">{icon}</span>
        <span>{label}</span>
      </div>
      {expandedSections[key] ? <FaChevronDown /> : <FaChevronRight />}
    </button>
  );

  const renderToggle = (id, name, label) => (
    <div className="cs-field">
      <div className="cs-toggle-wrapper">
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={!!editSettings[name]}
          onChange={handleChange}
          className="cs-toggle-input"
        />
        <label htmlFor={id} className="cs-toggle-track">
          <span className="cs-toggle-thumb" />
        </label>
        <label htmlFor={id}>{label}</label>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="cs-loading">
        <FaSpinner className="cs-spinner" />
        <p>{t('ticketFormSettings.loading', 'Chargement des paramètres du formulaire...')}</p>
      </div>
    );
  }

  return (
    <div className="cs-container tfs-container">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-left">
          <h1 className="cs-title">{t('ticketFormSettings.title', 'Formulaire de tickets')}</h1>
          {hasUnsavedChanges && (
            <span className="cs-unsaved-badge">
              {t('ticketFormSettings.unsaved', 'Modifications non enregistrées')}
            </span>
          )}
        </div>
        <div className="cs-header-actions">
          <button className="cs-btn cs-btn-outline" onClick={handleCopyEmbedCode}>
            <FaCode /> {t('ticketFormSettings.copyEmbed', "Copier le code d'intégration")}
          </button>
          {currentDoc && (
            <button className="cs-btn cs-btn-danger-outline" onClick={handleDelete}>
              <FaTrash /> {t('ticketFormSettings.delete', 'Supprimer')}
            </button>
          )}
          <button
            className="cs-btn cs-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <FaSpinner className="cs-spinner" />
                {t('ticketFormSettings.saving', 'Enregistrement...')}
              </>
            ) : (
              <>
                <FaSave /> {t('ticketFormSettings.save', 'Enregistrer')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="cs-notification cs-notification-error">
          <span>{error}</span>
          <button className="cs-notification-close" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="cs-notification cs-notification-success">
          <span><FaCheck /> {success}</span>
          <button className="cs-notification-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Per-brand form selector */}
      {brandEntries.length > 1 && (
        <div className="cs-chatbot-tabs">
          {brandEntries.map((entry) => {
            const doc = docForBrandKey(entry.key);
            return (
              <button
                key={entry.key || '__default__'}
                className={`cs-chatbot-tab ${selectedBrandKey === entry.key ? 'active' : ''}`}
                onClick={() => handleSelectBrand(entry.key)}
              >
                <div className="cs-tab-colors">
                  <span
                    className="cs-tab-color-dot"
                    style={{ backgroundColor: doc ? doc.primary_color : 'transparent' }}
                  />
                  {doc && (
                    <span
                      className="cs-tab-color-dot"
                      style={{ backgroundColor: doc.accent_color || '#2563eb' }}
                    />
                  )}
                </div>
                <div className="cs-tab-info">
                  <span className="cs-tab-name">{entry.name}</span>
                  {!doc && (
                    <span className="cs-tab-brand">
                      {t('ticketFormSettings.notConfigured', 'Non configuré')}
                    </span>
                  )}
                </div>
                {doc && ((doc.displayed ?? true) ? (
                  <FaEye className="cs-tab-status cs-tab-status-on" />
                ) : (
                  <FaEyeSlash className="cs-tab-status cs-tab-status-off" />
                ))}
              </button>
            );
          })}
        </div>
      )}

      {/* Main content */}
      <div className="cs-main">
        {/* Settings panel */}
        <div className="cs-settings-panel">
          {/* Texts section */}
          <div className="cs-section">
            {renderSectionHeader('texts', <FaCommentDots />, t('ticketFormSettings.sectionTexts', 'Textes'))}
            {expandedSections.texts && (
              <div className="cs-section-body">
                <div className="cs-field">
                  <label htmlFor="tfs-title-input">{t('ticketFormSettings.formTitle', 'Titre du formulaire')}</label>
                  <input
                    type="text"
                    id="tfs-title-input"
                    name="title"
                    value={editSettings.title}
                    onChange={handleChange}
                    placeholder={t('ticketFormSettings.formTitlePlaceholder', 'Ex. : Créer un nouveau ticket')}
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="tfs-intro-input">{t('ticketFormSettings.introText', "Texte d'introduction")}</label>
                  <textarea
                    id="tfs-intro-input"
                    name="intro_text"
                    value={editSettings.intro_text}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="tfs-success-input">{t('ticketFormSettings.successMessage', 'Message de confirmation')}</label>
                  <textarea
                    id="tfs-success-input"
                    name="success_message"
                    value={editSettings.success_message}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="tfs-button-input">{t('ticketFormSettings.buttonLabel', "Libellé du bouton d'envoi")}</label>
                  <input
                    type="text"
                    id="tfs-button-input"
                    name="button_label"
                    value={editSettings.button_label}
                    onChange={handleChange}
                    placeholder={t('ticketFormSettings.buttonLabelPlaceholder', 'Soumettre le Ticket')}
                  />
                </div>
                {renderToggle('tfs-displayed', 'displayed', t('ticketFormSettings.displayedLabel', 'Afficher le formulaire'))}
                {renderToggle('tfs-show-order-fields', 'show_order_fields', t('ticketFormSettings.showOrderFields', 'Afficher les champs commande / suivi'))}
                {renderToggle('tfs-classify-subjects', 'classify_inbound_subjects', t('ticketFormSettings.classifyInboundSubjects', 'Classer les sujets entrants par IA (e-mails, réseaux sociaux, chatbot)'))}
              </div>
            )}
          </div>

          {/* Design section */}
          <div className="cs-section">
            {renderSectionHeader('design', <FaPalette />, t('ticketFormSettings.sectionDesign', 'Apparence'))}
            {expandedSections.design && (
              <div className="cs-section-body">
                {[
                  ['primary_color', t('ticketFormSettings.primaryColor', 'Couleur principale')],
                  ['background_color', t('ticketFormSettings.backgroundColor', 'Couleur de fond')],
                  ['text_color', t('ticketFormSettings.textColor', 'Couleur du texte')],
                  ['accent_color', t('ticketFormSettings.accentColor', "Couleur d'accent")],
                ].map(([name, label]) => (
                  <div className="cs-color-row" key={name}>
                    <label htmlFor={`tfs-${name}`}>{label}</label>
                    <div className="cs-color-input-group">
                      <input
                        type="color"
                        id={`tfs-${name}`}
                        name={name}
                        value={editSettings[name]}
                        onChange={handleChange}
                        className="cs-color-picker"
                      />
                      <input
                        type="text"
                        value={editSettings[name]}
                        name={name}
                        onChange={handleChange}
                        className="cs-color-hex"
                        maxLength={7}
                      />
                    </div>
                  </div>
                ))}
                <div className="cs-field">
                  <label htmlFor="tfs-border-radius">
                    {t('ticketFormSettings.borderRadius', 'Arrondi des angles')} — {editSettings.border_radius}px
                  </label>
                  <div className="tfs-radius-group">
                    <input
                      type="range"
                      id="tfs-border-radius"
                      name="border_radius"
                      min="0"
                      max="32"
                      value={editSettings.border_radius}
                      onChange={handleChange}
                      className="cs-range"
                    />
                    <input
                      type="number"
                      name="border_radius"
                      min="0"
                      max="32"
                      value={editSettings.border_radius}
                      onChange={handleChange}
                      className="tfs-radius-number"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subjects section */}
          <div className="cs-section">
            {renderSectionHeader('subjects', <FaListUl />, `${t('ticketFormSettings.sectionSubjects', 'Sujets')} (${editSettings.subjects.length})`)}
            {expandedSections.subjects && (
              <div className="cs-section-body">
                <p className="tfs-hint">
                  {t('ticketFormSettings.subjectsHint', 'Les motifs proposés dans le menu « Sujet » du formulaire.')}
                </p>
                <ul className="tfs-subjects-list">
                  {editSettings.subjects.map((subject, index) => (
                    <li className="tfs-subject-row" key={index}>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => handleSubjectChange(index, e.target.value)}
                        className="tfs-subject-input"
                      />
                      <div className="tfs-subject-actions">
                        <button
                          type="button"
                          className="tfs-icon-btn"
                          onClick={() => handleMoveSubject(index, -1)}
                          disabled={index === 0}
                          title={t('ticketFormSettings.moveUp', 'Monter')}
                        >
                          <FaArrowUp />
                        </button>
                        <button
                          type="button"
                          className="tfs-icon-btn"
                          onClick={() => handleMoveSubject(index, 1)}
                          disabled={index === editSettings.subjects.length - 1}
                          title={t('ticketFormSettings.moveDown', 'Descendre')}
                        >
                          <FaArrowDown />
                        </button>
                        <button
                          type="button"
                          className="tfs-icon-btn tfs-icon-btn-danger"
                          onClick={() => handleRemoveSubject(index)}
                          disabled={editSettings.subjects.length <= 1}
                          title={t('ticketFormSettings.removeSubject', 'Supprimer le sujet')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="tfs-subject-add">
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubject();
                      }
                    }}
                    placeholder={t('ticketFormSettings.addSubjectPlaceholder', 'Nouveau sujet...')}
                    className="tfs-subject-input"
                  />
                  <button type="button" className="cs-btn cs-btn-secondary" onClick={handleAddSubject}>
                    <FaPlus /> {t('ticketFormSettings.addSubject', 'Ajouter')}
                  </button>
                </div>
                <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={handleResetSubjects}>
                  <FaUndo /> {t('ticketFormSettings.resetSubjects', 'Réinitialiser la liste par défaut')}
                </button>
              </div>
            )}
          </div>

          {/* Languages section */}
          <div className="cs-section">
            {renderSectionHeader('languages', <FaGlobe />, `${t('ticketFormSettings.sectionLanguages', 'Langues')}${editSettings.languages.length > 0 ? ` (${editSettings.languages.length})` : ''}`)}
            {expandedSections.languages && (
              <div className="cs-section-body">
                <p className="tfs-hint">
                  {t('ticketFormSettings.languagesHint', "Activez des langues d'affichage supplémentaires : les sujets et textes sont traduits automatiquement à l'enregistrement et affichés selon la langue du navigateur du visiteur, avec l'anglais par défaut. Les tickets conservent le sujet d'origine.")}
                </p>
                <div className="tfs-language-grid">
                  {SUPPORTED_FORM_LANGUAGES.map(({ code, label }) => (
                    <label key={code} className="tfs-language-option">
                      <input
                        type="checkbox"
                        checked={editSettings.languages.includes(code)}
                        onChange={() => handleToggleLanguage(code)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {editSettings.languages.length > 0 && (
                  currentDoc && Object.keys(currentDoc.translations || {}).length > 0 && !hasUnsavedChanges ? (
                    <p className="tfs-language-status">
                      {currentDoc.source_language && (
                        <>
                          {t('ticketFormSettings.detectedLanguage', 'Langue détectée du contenu')}
                          {' : '}
                          <strong>{currentDoc.source_language}</strong>
                          {' — '}
                        </>
                      )}
                      {t('ticketFormSettings.translationsReady', 'Traductions disponibles')}
                      {' : '}
                      <strong>{Object.keys(currentDoc.translations).join(', ')}</strong>
                    </p>
                  ) : (
                    <p className="tfs-language-status">
                      {t('ticketFormSettings.translationsPending', "Les traductions seront générées à l'enregistrement.")}
                    </p>
                  )
                )}
              </div>
            )}
          </div>

          {/* Embed code section */}
          {showEmbedCode && embedCode && (
            <div className="cs-section">
              {renderSectionHeader('embed', <FaCode />, t('ticketFormSettings.embedTitle', "Code d'intégration"))}
              {expandedSections.embed && (
                <div className="cs-section-body">
                  <p className="tfs-hint">
                    {t('ticketFormSettings.embedHint', 'Collez ce code dans le HTML de votre site pour afficher le formulaire de tickets.')}
                  </p>
                  <div className="cs-embed-code-block">
                    <code>{embedCode}</code>
                  </div>
                  <button
                    className="cs-btn cs-btn-outline cs-btn-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(embedCode);
                      setSuccess(t('ticketFormSettings.copySuccess', "Code d'intégration copié dans le presse-papiers"));
                      setTimeout(() => setSuccess(null), 3000);
                    }}
                  >
                    <FaCopy /> {t('ticketFormSettings.copy', 'Copier')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Live preview — the TicketFormPreview mirrors the embedded widget */}
        <div className="cs-preview-panel">
          <div className="cs-preview-header">
            <h3>{t('ticketFormSettings.previewTitle', 'Aperçu en direct')}</h3>
            <span className="cs-preview-hint">
              {t('ticketFormSettings.previewHint', 'Mise à jour instantanée')}
            </span>
          </div>
          <div className="tfs-preview-stage">
            {!editSettings.displayed && (
              <span className="tfs-preview-hidden-badge">
                {t('ticketFormSettings.hiddenBadge', 'Formulaire masqué')}
              </span>
            )}
            <TicketFormPreview settings={editSettings} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketFormSettings;
